import React, { useEffect, useState } from 'react';
import { Table, Button, IconButton, Stack, SelectPicker, Input, NumberInput, Message, Divider } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel } from '@/models/core.model';
import { DayOfWeekList, EnergyHub, EnergyHubHostingSlot } from '@/models/energy-hub.model';
import { setEnergyHubHostingSlots } from '@/services/energy-hub.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const { Column, HeaderCell, Cell } = Table;

/** An editable row. Times are "HH:mm" in the form and "HH:mm:ss" on the wire. */
interface SlotRow {
  key: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  maxCustomers?: number;
  note?: string;
}

const toHm = (t: string) => t?.slice(0, 5) ?? '';
const toHms = (t: string) => (t.length == 5 ? `${t}:00` : t);

const rowError = (r: SlotRow): string | undefined => {
  if (!r.startTime || !r.endTime) return 'Start and end are required.';
  if (r.endTime <= r.startTime) return 'End must be after start.';
  return undefined;
};

interface Props {
  hub: EnergyHub;
  onChanged: () => void;
}

const EnergyHubSlotEditor: React.FC<Props> = ({ hub, onChanged }) => {
  const [rows, setRows] = useState<SlotRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [nextKey, setNextKey] = useState(1);

  useEffect(() => {
    setRows(hub.hostingSlots.map((s, i) => ({
      key: i + 1,
      dayOfWeek: s.dayOfWeek,
      startTime: toHm(s.startTime),
      endTime: toHm(s.endTime),
      maxCustomers: s.maxCustomers ?? undefined,
      note: s.note ?? undefined,
    })));
    setNextKey(hub.hostingSlots.length + 1);
  }, [hub]);

  const update = (key: number, patch: Partial<SlotRow>) =>
    setRows(pre => pre.map(r => (r.key == key ? { ...r, ...patch } : r)));

  const addRow = () => {
    setRows(pre => [...pre, { key: nextKey, dayOfWeek: 6, startTime: '08:00', endTime: '10:00' }]);
    setNextKey(k => k + 1);
  };

  const hasErrors = rows.some(r => !!rowError(r));

  const handleSave = async () => {
    if (hasErrors) return;
    setErrorModel(undefined);
    setSaving(true);
    try {
      const body: EnergyHubHostingSlot[] = rows.map(r => ({
        dayOfWeek: r.dayOfWeek,
        startTime: toHms(r.startTime),
        endTime: toHms(r.endTime),
        maxCustomers: r.maxCustomers ? Number(r.maxCustomers) : undefined,
        note: r.note || undefined,
      }));
      const result = await setEnergyHubHostingSlots(hub.id, body);
      if (result.success) {
        toastSuccessMessage('Hosting slots saved');
        onChanged();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContent>
      <Message type="info" showIcon style={{ marginBottom: 16 }}>
        The venue's slow hours, when it is happy to host a club run (Hong Kong time). Shown to staff when planning Hub Runs;
        check-ins are not limited to these slots. Saving replaces the whole list.
      </Message>
      <Table autoHeight data={rows} rowKey="key" rowHeight={56}>
        <Column width={170}>
          <HeaderCell>Day</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => (
              <SelectPicker data={DayOfWeekList} searchable={false} cleanable={false} block value={r.dayOfWeek}
                onChange={v => update(r.key, { dayOfWeek: v ?? 0 })} />
            )}
          </Cell>
        </Column>
        <Column width={140}>
          <HeaderCell>Start</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => <Input type="time" value={r.startTime} onChange={v => update(r.key, { startTime: v })} />}
          </Cell>
        </Column>
        <Column width={140}>
          <HeaderCell>End</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => <Input type="time" value={r.endTime} onChange={v => update(r.key, { endTime: v })} />}
          </Cell>
        </Column>
        <Column width={130}>
          <HeaderCell>Max customers</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => (
              <NumberInput min={1} value={r.maxCustomers ?? null}
                onChange={v => update(r.key, { maxCustomers: v === null || v === '' ? undefined : Number(v) })} />
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Note</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => <Input value={r.note ?? ''} maxLength={500} onChange={v => update(r.key, { note: v })} />}
          </Cell>
        </Column>
        <Column width={200}>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: 8 }}>
            {(r: SlotRow) => (
              <Stack spacing={8}>
                <IconButton icon={<TrashIcon />} circle size="sm" color="red" appearance="ghost"
                  onClick={() => setRows(pre => pre.filter(x => x.key != r.key))} />
                {rowError(r) && <span style={{ color: '#e55', fontSize: 12 }}>{rowError(r)}</span>}
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 16 }} />}
      <Divider />
      <Stack justifyContent="space-between">
        <Button appearance="ghost" onClick={addRow}>Add slot</Button>
        <Button appearance="primary" onClick={handleSave} loading={saving} disabled={hasErrors}>Save slots</Button>
      </Stack>
    </PageContent>
  );
};

export default EnergyHubSlotEditor;
